import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Newspaper, BookOpen, Users, LogOut, Video, FileText,
  Eye, Trash2, Check, X, Download, Clock, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubmissions = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchSubmissions(); }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setSubmissions(await response.json());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/submissions/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        const updated = await response.json();
        setSubmissions(submissions.map(s => s.id === id ? updated : s));
        toast.success(`Statut mis à jour: ${status}`);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const downloadManuscript = async (submission) => {
    try {
      const response = await fetch(`${API_URL}/api/submissions/${submission.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Create download link from base64
        const link = document.createElement('a');
        link.href = data.data;
        link.download = data.filename;
        link.click();
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const deleteSubmission = async (id) => {
    if (!confirm('Supprimer cette soumission ?')) return;
    try {
      const response = await fetch(`${API_URL}/api/submissions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSubmissions(submissions.filter(s => s.id !== id));
        setSelectedSubmission(null);
        toast.success('Soumission supprimée');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const filteredSubmissions = filter === 'all' 
    ? submissions 
    : submissions.filter(s => s.status === filter);

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    reviewing: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };

  const statusLabels = {
    pending: 'En attente',
    reviewing: 'En cours d\'examen',
    accepted: 'Accepté',
    rejected: 'Refusé'
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
    { icon: Newspaper, label: 'Actualités', path: '/admin/news' },
    { icon: BookOpen, label: 'Livres', path: '/admin/books' },
    { icon: Video, label: 'Interviews', path: '/admin/videos' },
    { icon: FileText, label: 'Soumissions', path: '/admin/submissions', active: true },
    { icon: Users, label: 'Utilisateurs', path: '/admin/users' }
  ];

  return (
    <div className="min-h-screen bg-stone-100 flex">
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${item.active ? 'bg-hermes-600 text-white' : 'text-stone-300 hover:bg-stone-800'}`}>
                  <item.icon className="w-5 h-5" /><span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-stone-700">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors">
            <LogOut className="w-5 h-5" /><span>Déconnexion</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-serif font-bold text-stone-900">Soumissions de manuscrits</h2>
            <p className="text-stone-600 mt-1">{submissions.length} soumission(s) reçue(s)</p>
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'reviewing', 'accepted', 'rejected'].map(s => (
              <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1 text-sm rounded ${filter === s ? 'bg-hermes-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}>
                {s === 'all' ? 'Tous' : statusLabels[s]}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* List */}
          <div className="bg-white shadow-sm overflow-hidden">
            <div className="divide-y divide-stone-100 max-h-[70vh] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-stone-500">Chargement...</div>
              ) : filteredSubmissions.length === 0 ? (
                <div className="p-8 text-center text-stone-500">Aucune soumission</div>
              ) : (
                filteredSubmissions.map((submission) => (
                  <div key={submission.id} onClick={() => setSelectedSubmission(submission)} className={`p-4 cursor-pointer hover:bg-stone-50 ${selectedSubmission?.id === submission.id ? 'bg-hermes-50 border-l-4 border-hermes-600' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-stone-900">{submission.title}</h3>
                        <p className="text-sm text-stone-600">{submission.first_name} {submission.last_name}</p>
                        <p className="text-xs text-stone-400 mt-1">{new Date(submission.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${statusColors[submission.status]}`}>
                        {statusLabels[submission.status]}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="bg-white shadow-sm p-6">
            {selectedSubmission ? (
              <div>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-stone-900">{selectedSubmission.title}</h3>
                    <p className="text-hermes-600">{selectedSubmission.genre}</p>
                  </div>
                  <span className={`text-sm px-3 py-1 rounded ${statusColors[selectedSubmission.status]}`}>
                    {statusLabels[selectedSubmission.status]}
                  </span>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-xs text-stone-500 uppercase">Auteur</label>
                    <p className="font-medium">{selectedSubmission.first_name} {selectedSubmission.last_name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 uppercase">Email</label>
                    <p><a href={`mailto:${selectedSubmission.email}`} className="text-hermes-600 hover:underline">{selectedSubmission.email}</a></p>
                  </div>
                  {selectedSubmission.phone && (
                    <div>
                      <label className="text-xs text-stone-500 uppercase">Téléphone</label>
                      <p>{selectedSubmission.phone}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-stone-500 uppercase">Résumé</label>
                    <p className="text-sm text-stone-700 leading-relaxed">{selectedSubmission.summary}</p>
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 uppercase">Fichier</label>
                    <button onClick={() => downloadManuscript(selectedSubmission)} className="flex items-center gap-2 text-hermes-600 hover:text-hermes-700">
                      <Download className="w-4 h-4" />
                      {selectedSubmission.manuscript_filename}
                    </button>
                  </div>
                </div>

                <div className="border-t border-stone-200 pt-4">
                  <label className="text-xs text-stone-500 uppercase mb-2 block">Changer le statut</label>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => updateStatus(selectedSubmission.id, 'pending')} className="flex items-center gap-1 px-3 py-2 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded text-sm">
                      <Clock className="w-4 h-4" />En attente
                    </button>
                    <button onClick={() => updateStatus(selectedSubmission.id, 'reviewing')} className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-sm">
                      <Eye className="w-4 h-4" />En examen
                    </button>
                    <button onClick={() => updateStatus(selectedSubmission.id, 'accepted')} className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 hover:bg-green-200 rounded text-sm">
                      <CheckCircle className="w-4 h-4" />Accepter
                    </button>
                    <button onClick={() => updateStatus(selectedSubmission.id, 'rejected')} className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded text-sm">
                      <XCircle className="w-4 h-4" />Refuser
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-stone-200">
                  <button onClick={() => deleteSubmission(selectedSubmission.id)} className="flex items-center gap-2 text-red-600 hover:text-red-700 text-sm">
                    <Trash2 className="w-4 h-4" />Supprimer cette soumission
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center text-stone-400 py-12">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Sélectionnez une soumission pour voir les détails</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSubmissions;
